import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ObjectSchema } from "yup";

import {
  errors,
  isBeforeTimeString,
  TrafficTypeEnum,
  TTimeHHmm,
  yupLocalized,
} from "@ryde/shared";

import { IAvailabilityItem } from "./traffic-availability.models";

const skipIfDateTypePresent = (item: IAvailabilityItem): boolean => {
  return !!item.dayTypeIds?.length || !!item.dayGroupIds?.length;
};

const skipIfFromToPresent = (item: IAvailabilityItem): boolean => {
  return !!item.from || !!item.to;
};

export const useAvailabilityItemResolver = () => {
  const { t } = useTranslation();

  const availabilityInnerFormValidationSchema: ObjectSchema<
    Partial<IAvailabilityItem>
  > = useMemo(() => {
    return yupLocalized.object<Partial<IAvailabilityItem>>().shape({
      dayTypeIds: yupLocalized
        .array()
        .nullable()
        .test(
          "requiredCustomOneOf",
          t(errors.validation.requiredCustomOneOf),
          (dayTypeIds, ctx) => {
            const { dayGroupIds } = ctx.parent;

            if (skipIfFromToPresent(ctx.parent)) {
              return true;
            }

            return (
              (dayTypeIds && dayTypeIds.length > 0) ||
              (dayGroupIds && dayGroupIds.length > 0)
            );
          },
        ),
      dayGroupIds: yupLocalized
        .array()
        .nullable()
        .test(
          "requiredCustomOneOf",
          t(errors.validation.requiredCustomOneOf),
          (dayGroupIds, ctx) => {
            const { dayTypeIds } = ctx.parent;

            if (skipIfFromToPresent(ctx.parent)) {
              return true;
            }

            return (
              (dayGroupIds && dayGroupIds.length > 0) ||
              (dayTypeIds && dayTypeIds.length > 0)
            );
          },
        ),
      from: yupLocalized
        .string()
        .nullable()
        .test(
          "isFromRequired",
          t(errors.validation.required, { path: "from" }),
          (from, ctx) => {
            if (skipIfDateTypePresent(ctx.parent)) {
              return true;
            }

            return !!from;
          },
        )
        .test(
          "isBeforeTest",
          t(errors.validation.endTimeMustBeGreaterThenStartTime),
          (from, ctx) => {
            const { to } = ctx.parent;

            if (skipIfDateTypePresent(ctx.parent)) {
              return true;
            }

            return (
              !!from && isBeforeTimeString(from as TTimeHHmm, to as TTimeHHmm)
            );
          },
        ),
      to: yupLocalized
        .string()
        .nullable()
        .test(
          "isToRequired",
          t(errors.validation.required, { path: "to" }),
          (to, ctx) => {
            if (skipIfDateTypePresent(ctx.parent)) {
              return true;
            }

            return !!to;
          },
        )
        .test(
          "isBeforeTest",
          t(errors.validation.endTimeMustBeGreaterThenStartTime),
          (to, ctx) => {
            const { from } = ctx.parent;

            if (skipIfDateTypePresent(ctx.parent)) {
              return true;
            }

            return (
              !!to && isBeforeTimeString(from as TTimeHHmm, to as TTimeHHmm)
            );
          },
        ),
      traffic: yupLocalized
        .mixed<TrafficTypeEnum>()
        .oneOf(Object.values(TrafficTypeEnum))
        .required(),
    }) as ObjectSchema<Partial<IAvailabilityItem>>;
  }, [t]);
  return { availabilityInnerFormValidationSchema };
};
